import { AxiosError } from 'axios';
import { Dispatch, SetStateAction } from 'react';
import { ApiErrorCode } from './ApiError';

const FORBIDDEN_FALLBACK =
  "You don't have permission to do that. If you think this is wrong, contact a Kit Keeper admin.";
const UNAUTHORIZED_FALLBACK =
  'Your session has expired. Please sign in again to continue.';
const GENERIC_FALLBACK = 'An unexpected error occurred. Please try again.';

const IS_DEBUG =
  process.env.NODE_ENV === 'development' ||
  (typeof window !== 'undefined' &&
    window.location?.hostname === 'test.kitkeeper.co.uk');

function readStoredFailedRequestId(): string | undefined {
  try {
    const value = localStorage.getItem('last_failed_request_id');
    return value ? value : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Returns `undefined` for a code this build has never heard of, rather than falling off
 * the end of the switch.
 *
 * `ApiErrorCode` is a hand-kept copy of storage-api's `ErrorCode` and the two have drifted
 * before — `ACCOUNT_DEACTIVATED` shipped on the backend in September and was still missing
 * here. The type says this switch is exhaustive; the value comes off the wire, where the
 * type is a wish. Without the fallback an unknown code made this return `undefined`,
 * which the caller then showed the customer as no message at all.
 */
function messageFromCode(code: ApiErrorCode, requestId?: string): string | undefined {
  switch (code) {
    case 'UNAUTHENTICATED':
      return UNAUTHORIZED_FALLBACK;
    case 'FORBIDDEN':
      return FORBIDDEN_FALLBACK;
    case 'NOT_FOUND':
      return 'The item you were looking for could not be found.';
    case 'VALIDATION_FAILED':
      return "Please check the details you've entered and try again.";
    case 'CONFLICT':
      return 'This item was updated by someone else. Please refresh and try again.';
    case 'RATE_LIMITED':
      return 'Too many requests. Please wait a moment and try again.';
    case 'EXTERNAL_FAILURE':
      return "We couldn't reach an external service right now. Please try again in a moment.";
    case 'INTERNAL_ERROR':
      return requestId
        ? `Something went wrong on the server. Please try again. Reference: ${requestId}.`
        : 'Something went wrong on the server. Please try again.';
    case 'MAINTENANCE':
      return 'Kit Keeper is currently undergoing maintenance. Please try again shortly.';
    case 'ACCOUNT_DEACTIVATED':
      // The backend sends a message naming who to contact, and which of the two reasons
      // it is. That is worth more than anything canned, so defer to it.
      return undefined;
    case 'PAYMENT_METHOD_REQUIRED':
      return "There's no payment method on your account. Please add a card and try again.";
    default:
      return undefined;
  }
}

function serverFallback(requestId?: string): string {
  return requestId
    ? `Something went wrong on the server. Please try again. Reference: ${requestId}.`
    : 'Something went wrong on the server. Please try again.';
}

export default function errorMessage(
  setNetworkError: Dispatch<SetStateAction<string | undefined>>,
) {
  return (error: string | Error | AxiosError) => {
    // This is the last thing standing between a failure and what the customer reads, so
    // it must not be able to fail itself. It could: `JSON.stringify` throws on a circular
    // structure, and `null.hasOwnProperty` throws outright — and both arrive here, because
    // the caller is a `catch` block and a `catch` binding is whatever was thrown.
    let jsonError: Record<string, unknown> = {};
    try {
      jsonError = JSON.parse(JSON.stringify(error ?? {})) ?? {};
    } catch {
      jsonError = {};
    }
    if (typeof jsonError !== 'object' || jsonError === null) jsonError = {};
    let userMessage: string;
    let technicalDetail: string | undefined;

    if (jsonError.hasOwnProperty('config')) {
      const axiosError = error as AxiosError;
      const status = axiosError.response?.status;
      const data = axiosError.response?.data;
      const code: ApiErrorCode | undefined = data?.code;
      const envelopeRequestId: string | undefined = data?.requestId;
      const backendMessage: string | undefined = data?.error || data?.message;

      technicalDetail = backendMessage;

      if (code) {
        // A code we know wins, because its wording is written for the customer. A code we
        // do not know falls through to whatever the backend said, and only then to the
        // generic — the one thing that must not happen is showing nothing.
        userMessage =
          messageFromCode(code, envelopeRequestId) || backendMessage || GENERIC_FALLBACK;
      } else if (status && status >= 500) {
        const fallbackRequestId =
          envelopeRequestId || readStoredFailedRequestId();
        userMessage = serverFallback(fallbackRequestId);
      } else if (status === 403) {
        userMessage = backendMessage || FORBIDDEN_FALLBACK;
      } else if (status === 401) {
        userMessage = backendMessage || UNAUTHORIZED_FALLBACK;
      } else {
        userMessage = backendMessage || GENERIC_FALLBACK;
      }
    } else if (error instanceof Error) {
      // `Error.message` is not enumerable, so the JSON round-trip above turns a plain
      // `new Error('Offline')` into `{}` — which meant the `hasOwnProperty('message')`
      // branch below could never match one, and the raw Error object was handed to a
      // setter typed `string | undefined` and then rendered. React throws on an object
      // child, so the customer got a blank screen rather than a message.
      userMessage = error.message;
      technicalDetail = userMessage;
    } else if (jsonError && typeof jsonError === 'object' && 'message' in jsonError) {
      userMessage = String((jsonError as { message: unknown }).message);
      technicalDetail = userMessage;
    } else if (typeof error === 'string') {
      userMessage = error;
      technicalDetail = userMessage;
    } else {
      // Something that is neither an axios error, an Error, nor a string. There is
      // nothing to read, and anything but a string here ends up rendered.
      userMessage = GENERIC_FALLBACK;
    }

    if (IS_DEBUG && technicalDetail && technicalDetail !== userMessage) {
      console.error('[errorMessage] technical detail:', technicalDetail);
    } else if (IS_DEBUG) {
      console.error(userMessage);
    }

    setNetworkError(userMessage);
  };
}
